import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { quoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { handleQuoteResponse } from 'epic/workspace/ordersBasket/orderOperations/common/orderOperations.helper';
import { ofType } from 'redux-observable';
import { merge, type Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take, takeUntil, timeout } from 'rxjs/operators';
import { Action } from 'state/actions';
import { OrdersBasketFormQuoteFailed, ordersBasketFormQuoteTimeout } from 'state/orders-basket/actions';
import type { OrdersBasketQuoteResponse } from 'state/orders-basket/models';
import { ordersBasketFormQuoteFailedThunk } from 'state/orders-basket/thunks';
import type { EpicWithThunk, Thunk } from 'state/state.common';
import type { IHttpClient } from 'utils/http.old/httpClient.model';
import { fatEventApiConnector } from 'utils/streaming/api';
import { ordersBasketQuoteResult } from 'utils/streaming/messages';
import type { Streaming } from 'utils/streaming/model';

function quoteStreamedForBooking(
  streaming: Streaming,
  oldHttpClient: IHttpClient,
  quoteId: string,
): Observable<Action | Thunk<void>> {
  return ordersBasketQuoteResult(streaming).pipe(
    filter(event => event.quoteId === quoteId),
    mergeMap(lightEvent =>
      fatEventApiConnector(oldHttpClient)
        .getFatEvent<OrdersBasketQuoteResponse>(lightEvent)
        .pipe(
          map(quoteResponse => handleQuoteResponse(quoteResponse, false)),
          catchError((error: Error) =>
            throwError(new apiErrorHelper.EpicApiError(apiErrorHelper.defaultTechnicalErrorKey, error)),
          ),
        ),
    ),
  );
}

export function quoteResultFailed(
  action$: Observable<Action>,
  formId: string,
): Observable<OrdersBasketFormQuoteFailed> {
  return action$.pipe(
    ofType('ORDERS_BASKET_FORM_QUOTE_FAILED'),
    filter(event => event.formId === formId),
  );
}

export function quoteResultReceivedForBookingEpic(): EpicWithThunk {
  return (action$, state$, { oldHttpClient, streaming, getSgmeConfiguration, frontLogger }) =>
    action$.pipe(
      ofType('BOOKING_ACKED'),
      filter(action => action.workspace === 'ordersBasket'),
      mergeMap(({ tabId, quoteId, formId }) => {
        const bookingReportTimeoutMs = getSgmeConfiguration().bookingReportTimeoutMs;

        // Edge case
        const quoteResultFailed$ = quoteResultFailed(action$, formId);
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);

        // Happy case
        const quoteStreamed$ = quoteStreamedForBooking(streaming, oldHttpClient, quoteId);

        return quoteStreamed$.pipe(
          timeout(bookingReportTimeoutMs),
          takeUntil(merge(quoteOrTabRemoved$, quoteResultFailed$)),
          take(1),
          catchError((error: Error) => {
            frontLogger.error('An error occurred inside tradeResultReceivedEpic {@FrontError}', error);
            if (error.name === 'TimeoutError') {
              return of(ordersBasketFormQuoteTimeout(formId));
            }
            return of(ordersBasketFormQuoteFailedThunk(formId, apiErrorHelper.toApiErrorMessage(error)));
          }),
        );
      }),
    );
}
